import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import {
    CaptureResultModalContent,
    CaptureModalTitle,
    CaptureModalWrapper,
    CaptureModalActions,
    DownloadIcon,
    CopyIcon,
} from '@/components/Hypertracker/HypertrackerCapture/CaptureBannerStyled';
import { useToastCtx } from '@/context/ToastContext';
import { Button, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/system';
import {
    copyBlobToClipboard,
    DecoratedCaptureResult,
    downloadBlob,
    makeObjectUrl,
} from '@/components/Hypertracker/HypertrackerCapture/captureHelpers';
import { darken } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { Image } from '@/pages/Settings/PaymentsTab/PaymentsTabStyled';
import useFormatDate from '@/hooks/useFormatDate';
import { useLocation } from 'react-router';

interface CaptureResultModalProps {
    open: DecoratedCaptureResult | null;
    stop: () => void;
    setResultImage: (image: DecoratedCaptureResult | null) => void;
}

const CaptureResultModal: React.FC<CaptureResultModalProps> = ({ open, stop, setResultImage }) => {
    const { t } = useTranslation('hypertracker_capture');
    const { toggleToast } = useToastCtx();
    const theme = useTheme();
    const [copied, setCopied] = useState(false);
    const { pathname } = useLocation();
    const { format } = useFormatDate();

    const walletName = useMemo(() => {
        const pathParts = pathname.split('/');
        return pathParts?.at(-1) || '';
    }, [pathname]);

    const filename = useMemo(() => {
        return `HyperTracker-capture-${format(new Date(), 'dd-MMM-yyyy')}-${walletName}`;
    }, [walletName, format]);

    const onDownloadClick = () => {
        try {
            if (!open) {
                throw new Error('No image to download');
            }
            downloadBlob(open?.jpg, filename);
        } catch (error) {
            toggleToast(true, {
                label: t('modal_download_error'),
                severity: 'error',
                icon: 'error',
            });
        }
    };
    const onCopyClick = async () => {
        try {
            if (!open) {
                throw new Error('No image to copy');
            }
            await copyBlobToClipboard(open?.blob);
            setCopied(true);
        } catch (error) {
            toggleToast(true, {
                label: t('modal_copy_error'),
                severity: 'error',
                icon: 'error',
            });
        } finally {
            setTimeout(() => setCopied(false), 500);
        }
    };
    const onCloseClick = () => {
        stop();
        setResultImage(null);
    };

    const buttonSx = useMemo(() => {
        return {
            textTransform: 'none',
            borderRadius: '4px',
            border: `1px solid ${theme.palette.text.secondary}`,
            backgroundColor: theme.palette.backgrounds.bg4,
            padding: '6px 20px',
            '&:hover': {
                borderRadius: '4px',
                backgroundColor: darken(theme.palette.backgrounds.bg4, 0.1),
            },
        };
    }, [theme]);

    return (
        <Dialog open={Boolean(open)} fullWidth maxWidth="sm">
            <CaptureModalWrapper>
                <CaptureModalTitle>
                    <Typography>{t('modal_title')}</Typography>
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'flex-end'}
                        gap={'12px'}
                    >
                        <Typography variant={'p14'} color={'customText.text1'}>
                            {`${open?.width}x${open?.height}`}
                        </Typography>
                        <Typography variant={'p14'} color={'customText.text1'}>
                            •
                        </Typography>
                        <Typography variant={'p14'} color={'customText.text1'}>
                            {open?.kb}KB
                        </Typography>
                    </Stack>
                </CaptureModalTitle>
                <CaptureResultModalContent>
                    {open && <Image src={makeObjectUrl(open?.blob)} alt={filename} />}
                </CaptureResultModalContent>
                <CaptureModalActions>
                    <Button
                        onClick={onDownloadClick}
                        variant={'contained'}
                        sx={{
                            ...buttonSx,
                        }}
                        endIcon={<DownloadIcon />}
                    >
                        {t('modal_download')}
                    </Button>
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'flex-end'}
                        gap={'12px'}
                    >
                        <Button
                            onClick={onCopyClick}
                            variant={'contained'}
                            endIcon={
                                copied ? (
                                    <CheckIcon
                                        sx={{
                                            width: '16px',
                                            height: 'auto',
                                            path: { fill: '#000' },
                                        }}
                                    />
                                ) : (
                                    <CopyIcon />
                                )
                            }
                            sx={
                                {
                                    ...buttonSx,
                                    backgroundColor: theme.palette.hypertracker.light,
                                    border: `1px solid ${theme.palette.hypertracker.light}`,
                                    '&:hover': {
                                        borderRadius: '4px',
                                        backgroundColor: darken(
                                            theme.palette.hypertracker.light as string,
                                            0.15,
                                        ),
                                    },
                                } as any
                            }
                        >
                            <Typography variant={'p14'} color={'common.black'}>
                                {t('modal_copy')}
                            </Typography>
                        </Button>
                        <Button
                            onClick={onCloseClick}
                            variant={'contained'}
                            sx={{
                                ...buttonSx,
                            }}
                        >
                            {t('modal_close')}
                        </Button>
                    </Stack>
                </CaptureModalActions>
            </CaptureModalWrapper>
        </Dialog>
    );
};

export default CaptureResultModal;
